import React, { useState, useEffect } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import {
  Tabs,
  Tab,
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  TextField,
} from "@mui/material";
import { Dashboard, People, Settings, Description } from "@mui/icons-material";
import TechCamp from "../Calendars/TechCamp";
import FDPEvent from "../Calendars/FDPEvent";
import EDPEvent from "../Calendars/EDPEvent";
import EDPBookCalendar from "../Calendars/EDPBookCalendar";
import TrainerStatusCards from "./TrainerStatusCards";
import ComingSoonPage from "../../../components/comingsoon/ComingSoon";
import AssignedInstitutesTable from "./AssignedInstitutesTable";
import TrainerDocumentEditor from "../TrainerDocumentEditor";

function TabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function DashboardComponent() {
  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Dashboard Overview
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ bgcolor: "#e3f2fd" }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Users
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                1,234
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                ↑ 12% from last month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ bgcolor: "#f3e5f5" }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Revenue
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                $45.2K
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                ↑ 8% from last month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ bgcolor: "#fff3e0" }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Active Projects
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                28
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                ↓ 3% from last month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card sx={{ bgcolor: "#e8f5e9" }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completion Rate
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                87%
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                ↑ 5% from last month
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Recent Activity
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1,
                  bgcolor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Dashboard color="primary" />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    New project created
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    2 hours ago
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1,
                  bgcolor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <People color="success" />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    5 new team members added
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    5 hours ago
                  </Typography>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1,
                  bgcolor: "#f5f5f5",
                  borderRadius: 1,
                }}
              >
                <Description color="warning" />
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Report generated successfully
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    1 day ago
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}

function SettingsComponent() {
  const [formData, setFormData] = useState({
    companyName: "Acme Corporation",
    email: "admin@acme.com",
    phone: "+1 234 567 8900",
  });

  const handleChange = (field) => (event) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Settings & Configuration
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Company Information
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  label="Company Name"
                  value={formData.companyName}
                  onChange={handleChange("companyName")}
                  fullWidth
                  variant="outlined"
                />
                <TextField
                  label="Email Address"
                  value={formData.email}
                  onChange={handleChange("email")}
                  fullWidth
                  variant="outlined"
                  type="email"
                />
                <TextField
                  label="Phone Number"
                  value={formData.phone}
                  onChange={handleChange("phone")}
                  fullWidth
                  variant="outlined"
                />
                <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                  <Button variant="contained" color="primary">
                    Save Changes
                  </Button>
                  <Button variant="outlined" color="secondary">
                    Cancel
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Notification Preferences
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                  }}
                >
                  <Typography>Email Notifications</Typography>
                  <Button variant="contained" size="small">
                    Enabled
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                  }}
                >
                  <Typography>SMS Alerts</Typography>
                  <Button variant="outlined" size="small">
                    Disabled
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 2,
                    bgcolor: "#f5f5f5",
                    borderRadius: 1,
                  }}
                >
                  <Typography>Push Notifications</Typography>
                  <Button variant="contained" size="small">
                    Enabled
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Settings sx={{ fontSize: 60, color: "#1976d2", mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 1 }}>
                Quick Actions
              </Typography>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 2 }}
              >
                <Button variant="outlined" fullWidth>
                  Reset Password
                </Button>
                <Button variant="outlined" fullWidth>
                  Export Data
                </Button>
                <Button variant="outlined" fullWidth>
                  View Logs
                </Button>
                <Button variant="outlined" color="error" fullWidth>
                  Delete Account
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default function ZohoTabs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const getInitialTab = () => {
    // 1. URL search param: ?tab=1 or ?tab=fdp, etc.
    const tabParam = searchParams.get("tab");
    if (tabParam !== null) {
      const lower = tabParam.toLowerCase();
      if (lower === "1" || lower === "fdp") return 1;
      if (lower === "2" || lower === "institutes" || lower === "assigned") return 2;
      if (lower === "3" || lower === "prerequisites") return 3;
      if (lower === "0" || lower === "techcamp") return 0;
      const parsed = parseInt(tabParam, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 3) return parsed;
    }
    // 2. Navigation state: { tab: 1 }
    if (location.state && location.state.tab !== undefined) {
      const parsed = parseInt(location.state.tab, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 3) return parsed;
    }
    // 3. Session storage
    const saved = sessionStorage.getItem("trainer_active_tab");
    if (saved !== null) {
      const parsed = parseInt(saved, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 3) return parsed;
    }
    return 0;
  };

  const [value, setValue] = useState(getInitialTab);

  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam !== null) {
      const lower = tabParam.toLowerCase();
      let targetTab = 0;
      if (lower === "1" || lower === "fdp") targetTab = 1;
      else if (lower === "2" || lower === "institutes" || lower === "assigned") targetTab = 2;
      else if (lower === "3" || lower === "prerequisites") targetTab = 3;
      else {
        const parsed = parseInt(tabParam, 10);
        if (!isNaN(parsed) && parsed >= 0 && parsed <= 3) targetTab = parsed;
      }
      if (targetTab !== value) {
        setValue(targetTab);
        sessionStorage.setItem("trainer_active_tab", String(targetTab));
      }
    } else if (location.state && location.state.tab !== undefined) {
      const parsed = parseInt(location.state.tab, 10);
      if (!isNaN(parsed) && parsed >= 0 && parsed <= 3 && parsed !== value) {
        setValue(parsed);
        sessionStorage.setItem("trainer_active_tab", String(parsed));
      }
    }
  }, [searchParams, location.state]);

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
    sessionStorage.setItem("trainer_active_tab", String(newValue));
    setSearchParams({ tab: newValue }, { replace: true });
  };

  return (
    <Box
      sx={{ width: "100%", maxWidth: "1536px", mt: 4, mb: 4, mx: "auto", p: 2 }}
    >
      {/* Zoho-Style Tabs */}
      <Box
        sx={{
          borderBottom: "1px solid #e5e7eb",
          bgcolor: "white",
        }}
      >
        <Tabs
          value={value}
          onChange={handleTabChange}
          aria-label="zoho style tabs"
          sx={{
            minHeight: "auto",
            "& .MuiTabs-indicator": {
              backgroundColor: "#4a90e2",
              height: 3,
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              minHeight: 48,
              minWidth: "auto",
              px: 3,
              py: 1.5,
              color: "#6b7280",
              transition: "all 0.2s ease",
              mr: 1,
              "&.Mui-selected": {
                color: "#111827",
                fontWeight: 600,
              },
              "&:hover": {
                color: "#374151",
                bgcolor: "#f9fafb",
              },
            },
            "& .MuiTabs-flexContainer": {
              gap: "4px",
            },
          }}
        >
          {/* <Tab
            icon={<Dashboard sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Dashboard"
          /> */}
          <Tab
            // icon={<Settings sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Tech Camp"
          />
          <Tab
            // icon={<Settings sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="FDP"
          />
          {/* <Tab
            // icon={<Settings sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="EDP"
          /> */}
          <Tab
            iconPosition="start"
            label="Assigned Institutes"
          />
          <Tab
            iconPosition="start"
            label="Prerequisites"
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ bgcolor: "#f9fafb", minHeight: "400px" }}>
        {/* <TabPanel value={value} index={0}>
          <TrainerStatusCards />
         
        </TabPanel> */}
        <TabPanel value={value} index={0}>
          <TechCamp />
        </TabPanel>
        <TabPanel value={value} index={1}>
          <FDPEvent />
        </TabPanel>
        {/* <TabPanel value={value} index={2}>
          <EDPBookCalendar />
        </TabPanel> */}
        <TabPanel value={value} index={2}>
          <AssignedInstitutesTable
            onNavigateToEDP={(institute) => {
              setValue(2);
            }}
          />
        </TabPanel>
        <TabPanel value={value} index={3}>
          <TrainerDocumentEditor />
        </TabPanel>
      </Box>
    </Box>
  );
}
